import { Fragment, useEffect, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { Footer, Topbar, SEO } from '@components'
import { CLIENT_CODE, SITE_URL } from '@constants'
import { useStoreActions, useStoreState } from '@stores'

export const Directory = () => {
	const [searchParams] = useSearchParams()
	const [selectedCategory, setSelectedCategory] = useState(
		searchParams.get('category') || 'All'
	)
	const [searchQuery, setSearchQuery] = useState('')

	useEffect(() => {
		const cat = searchParams.get('category')
		if (cat) setSelectedCategory(cat)
	}, [searchParams])

	const { fetchAllOutlet } = useStoreActions(action => action.outlet)
	const { outlets } = useStoreState(state => state.outlet)
	const { fetchAllCategory } = useStoreActions(action => action.category)
	const { categories: categoryItems } = useStoreState(state => state.category)

	const filteredOutlets = outlets
		? outlets.filter(outlet => {
				const matchesCategory =
					selectedCategory === 'All' ||
					(outlet.store_category?.name &&
						outlet.store_category.name
							.toLowerCase()
							.includes(selectedCategory.toLowerCase()))
				const matchesSearch =
					(outlet.name &&
						outlet.name.toLowerCase().includes(searchQuery.toLowerCase())) ||
					(outlet.note &&
						outlet.note.toLowerCase().includes(searchQuery.toLowerCase()))
				return matchesCategory && matchesSearch
			})
		: []

	const categories = [
		'All',
		...categoryItems.map(item => item.name).filter(Boolean)
	]

	useEffect(() => {
		fetchAllOutlet({
			page: 1,
			limit: 100,
			client_code: CLIENT_CODE
		})
		fetchAllCategory({
			page: 1,
			limit: 100,
			client_code: CLIENT_CODE
		})
	}, [fetchAllOutlet, fetchAllCategory])

	const directoryJsonLd = {
		'@context': 'https://schema.org',
		'@type': 'ItemList',
		name: 'Tenant Directory — Botani Square Mall',
		url: `${SITE_URL}/directory`,
		numberOfItems: (outlets || []).length,
		itemListElement: (outlets || []).slice(0, 50).map((o, i) => ({
			'@type': 'ListItem',
			position: i + 1,
			url: `${SITE_URL}/directory/${o.slug}`,
			name: o.name
		}))
	}

	const breadcrumb = {
		'@context': 'https://schema.org',
		'@type': 'BreadcrumbList',
		itemListElement: [
			{ '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE_URL}/` },
			{
				'@type': 'ListItem',
				position: 2,
				name: 'Directory',
				item: `${SITE_URL}/directory`
			}
		]
	}

	return (
		<Fragment>
			<SEO
				title="Tenant Directory — Stores & Restaurants"
				description="Browse every tenant at Botani Square Mall Bogor: fashion, dining, kids, electronics, beauty and more. Find floor and contact for each store."
				path="/directory"
				keywords={[
					'Botani Square tenants',
					'directory mall Bogor',
					'store list Botani Square',
					'restoran Botani Square'
				]}
				jsonLd={[directoryJsonLd, breadcrumb]}
			/>
			<Topbar />

			{/* Hero Section with Banner Image */}
			<section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-24">
				{/* Background Banner Image */}
				<div className="absolute inset-0">
					<img
						src={require('@assets/images/banner-2.png')}
						alt="Directory Banner"
						className="w-full h-full object-cover"
					/>
					{/* Overlay for better text readability */}
					<div className="absolute inset-0 bg-black bg-opacity-60"></div>
				</div>

				{/* Content */}
				<div className="relative z-10 text-center text-white px-6 max-w-4xl mx-auto">
					<h1 className="text-5xl md:text-7xl font-bold mb-6">
						Tenant Directory
					</h1>
					<p className="text-lg md:text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
						Discover all the amazing stores, restaurants, and services that make
						Botani Square your ultimate destination.
					</p>

					{/* Search Bar */}
					<div className="relative max-w-2xl mx-auto mb-6 sm:mb-8">
						<input
							type="text"
							placeholder="Search stores or restaurant..."
							value={searchQuery}
							onChange={e => setSearchQuery(e.target.value)}
							className="w-full px-4 sm:px-6 py-3 sm:py-4 pl-12 sm:pl-14 bg-white/90 text-gray-800 rounded-lg text-base sm:text-lg focus:outline-none focus:ring-2 focus:ring-primary"
						/>
						<svg
							className="absolute left-3 sm:left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 sm:w-6 sm:h-6 text-gray-500"
							fill="none"
							stroke="currentColor"
							viewBox="0 0 24 24"
						>
							<path
								strokeLinecap="round"
								strokeLinejoin="round"
								strokeWidth={2}
								d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
							/>
						</svg>
					</div>

					{/* Category Filters */}
					<div className="flex flex-wrap justify-center gap-2 sm:gap-3 mb-6 sm:mb-8">
						{categories.map(category => (
							<button
								key={category}
								onClick={() => setSelectedCategory(category)}
								className={`px-4 sm:px-6 py-2 sm:py-3 rounded-lg font-medium transition-colors text-sm sm:text-base ${
									selectedCategory === category
										? 'bg-primary text-white'
										: 'bg-white/20 text-white hover:bg-white/30'
								}`}
							>
								{category}
							</button>
						))}
					</div>
				</div>
			</section>

			{/* Tenant Listings */}
			<section className="py-20 bg-white">
				<div className="max-w-screen-xl mx-auto px-6">
					{filteredOutlets && filteredOutlets.length > 0 ? (
						<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
							{filteredOutlets.map((outlet, index) => (
								<div
									key={outlet.id || index}
									className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow"
								>
									{/* Tenant Image */}
									<div className="h-48 bg-white flex items-center justify-center overflow-hidden">
										{outlet.image ? (
											<img
												src={outlet.image}
												alt={outlet.name || 'Store Logo'}
												className="w-full h-full object-contain p-4"
											/>
										) : (
											<div className="text-6xl">🏪</div>
										)}
									</div>

									{/* Tenant Info */}
									<div className="p-6">
										<div className="text-xs text-gray-500 uppercase tracking-wider mb-2">
											{outlet.store_category?.name || 'STORE'}
										</div>
										<h3 className="text-xl font-bold text-gray-800 mb-2">
											{outlet.name || 'Store Name'}
										</h3>
										<p className="text-gray-600 text-sm mb-4">
											{outlet.note || 'Premium store with exclusive products'}
										</p>

										{/* Location */}
										<div className="flex items-center text-sm text-gray-500 mb-2">
											<svg
												className="w-4 h-4 mr-2"
												fill="currentColor"
												viewBox="0 0 20 20"
											>
												<path
													fillRule="evenodd"
													d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z"
													clipRule="evenodd"
												/>
											</svg>
											{outlet.floor || 'Ground Floor'}
										</div>

										{/* Phone */}
										<div className="flex items-center text-sm text-gray-500 mb-4">
											<svg
												className="w-4 h-4 mr-2"
												fill="currentColor"
												viewBox="0 0 20 20"
											>
												<path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
											</svg>
											Contact Store
										</div>

										{/* View Details Button */}
										<Link
											to={`/directory/${outlet.slug}`}
											className="w-full bg-gray-100 hover:bg-gray-200 text-gray-800 py-3 rounded-lg font-medium transition-colors flex items-center justify-center"
										>
											View Details
											<svg
												className="w-4 h-4 ml-2"
												fill="currentColor"
												viewBox="0 0 20 20"
											>
												<path
													fillRule="evenodd"
													d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z"
													clipRule="evenodd"
												/>
											</svg>
										</Link>
									</div>
								</div>
							))}
						</div>
					) : (
						<div className="text-center py-16">
							<div className="text-6xl mb-4">🏪</div>
							<h3 className="text-2xl font-bold text-gray-800 mb-4">
								No Stores Available
							</h3>
							<p className="text-gray-600 text-lg">
								Check back later for store listings!
							</p>
						</div>
					)}
				</div>
			</section>

			{/* Footer */}
			<Footer />
		</Fragment>
	)
}
